import { Route, Routes, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Switch from '@mui/material/Switch';
import { Box, Button, Chip, FormControlLabel, Grid, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { lazy, useEffect, useMemo, useState } from 'react';
import { checkPermissions, PRODUCTS_WRITE } from '../../shared/utils/permissions';
import { useUserPermissions } from '../../apis/user-details';
import styled from 'styled-components';
import { StyledLink, StyledRouterLink } from '../../shared/StyledLink/StyledLink';
import { IngredientDosingDialog } from './components/IngredientDosingDialog';
import { Search } from '@mui/icons-material';
import Fuse from 'fuse.js';
import StyledDataGrid from '../../shared/DataGrid/StyledDataGrid';
import { useAllDosings, useAllIngredients, useAllProductSizes } from '../../apis/products-api';
import { GridCopyButton } from '../../shared/utils/gridUtils';
import { getIngredientCategoryName, getSizeName } from '../../apis/utils';

const IngredientDetails = lazy(() => import('./IngredientDetails'));

export const IngredientsPage = (): JSX.Element => {
  const { t } = useTranslation();
  const { roles: userPermissions } = useUserPermissions();
  const navigate = useNavigate();

  const [showNotActive, setShowNotActive] = useState(false);
  const [showRecipeOnly, setShowRecipeOnly] = useState(false);
  const [ingredientDetailsDialogOpen, setIngredientDetailsDialogOpen] = useState(false);
  const [ingredientDosingDialogId, setIngredientDosingDialogId] = useState();
  const [searchValue, setSearchValue] = useState('');

  const { data: dosingDetails } = useAllDosings();
  const { data: productSizes } = useAllProductSizes();

  const toggleNotActive = () => {
    setShowNotActive((prev) => !prev);
  };

  const toggleRecipeOnly = () => setShowRecipeOnly((prev) => !prev);

  const { data: initialData, isLoading } = useAllIngredients(true, {
    select: (data) => {
      return data?.map((ingredient) => {
        return {
          ...ingredient,
          dosingSummary: ingredient.dosings?.length,
          id: ingredient.ingredientId,
        };
      });
    },
  });
  const notActiveCount = (initialData ?? []).filter((ingredient) => !ingredient.isActive).length;
  const recipeOnlyCount = (initialData ?? []).filter((i) => {
    if (showNotActive) {
      return i.isRecipeOnlyIngredient;
    }
    return i.isRecipeOnlyIngredient && i.isActive;
  }).length;

  const [filteredRows, setFilteredRows] = useState(initialData ?? []);

  const fuse = useMemo(
    () =>
      new Fuse(initialData || [], {
        minMatchCharLength: 3,
        threshold: 0.2,
        ignoreLocation: true,
        keys: ['name', 'ingredientId', 'ingredientCategoryName'],
      }),
    [initialData]
  );

  const handleNewIngredient = () => {
    navigate('/ingredients/new');
  };

  useEffect(() => {
    let activeData = showNotActive ? initialData : initialData?.filter((x) => x.isActive);
    activeData = showRecipeOnly ? activeData?.filter((i) => i.isRecipeOnlyIngredient) : activeData;

    if (searchValue.length >= 3) {
      fuse.setCollection(activeData || []);
      setFilteredRows([...fuse.search(searchValue).map((result) => result.item)]);
    }
    if (searchValue.length < 3) {
      setFilteredRows(activeData || []);
    }
  }, [fuse, initialData, searchValue, showNotActive, setFilteredRows, showRecipeOnly]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'id',
      flex: 1,
      renderCell: (params) => (
        <>
          <GridCopyButton params={params} />{' '}
          <StyledRouterLink to={`/ingredients/${params.id}`}>{params.id}</StyledRouterLink>
        </>
      ),
    },
    {
      field: 'name',
      headerName: t('product.product'),
      flex: 3,
      renderCell: (params) => {
        return (
          <IngredientInfo>
            <StyledRouterLink to={`/ingredients/${params.id}`}>{params.value}</StyledRouterLink>
            {dosingDetails && (
              <CategoryText>{getIngredientCategoryName(params.row.ingredientCategoryId, dosingDetails)}</CategoryText>
            )}
          </IngredientInfo>
        );
      },
    },
    {
      field: 'dosingSummary',
      headerName: t('global.dosing'),
      flex: 2,
      renderCell: (params) => {
        const handleClick = () => {
          setIngredientDetailsDialogOpen(true);
          setIngredientDosingDialogId(params.row.ingredientId);
        };
        if (params.value == 0) {
          return (
            <ErrorLink onClick={handleClick}>
              {params.value} {t('dosings')}
            </ErrorLink>
          );
        }
        if (params.value == 1) {
          return (
            <StyledLink onClick={handleClick}>
              {params.value} {t('dosings')} - {getSizeName(params.row.dosings[0].productSizeId, productSizes)}
            </StyledLink>
          );
        }
        return (
          <StyledLink onClick={handleClick}>
            {params.value} {t('dosings')}
          </StyledLink>
        );
      },
    },
    {
      field: 'description',
      headerName: t('product.description'),
      flex: 6,
    },
    { field: 'displayOrder', headerName: t('ingredientDetails.ingredientOrderNumber'), flex: 1 },
    {
      field: 'isActive',
      headerName: t('Status'),
      flex: 1,
      renderCell: (params) => {
        return (params.value as boolean) ? (
          <Chip color="primary" label={t('shared.activeLabel.active')} />
        ) : (
          <Chip color="error" label={t('shared.activeLabel.inactive')} />
        );
      },
    },
  ];

  return (
    <Routes>
      <Route path=":id" element={<IngredientDetails />} />
      <Route
        index
        element={
          <>
            {ingredientDosingDialogId && (
              <IngredientDosingDialog
                title="Ingredient dosings"
                ingredientId={ingredientDosingDialogId as unknown as number}
                open={ingredientDetailsDialogOpen}
                onDialogClose={() => setIngredientDetailsDialogOpen(false)}
              />
            )}
            <TopBar>
              <Typography variant="h1">{t('ingredients.title')}</Typography>
              <Button
                disabled={!checkPermissions(userPermissions || [], [PRODUCTS_WRITE])}
                variant="contained"
                onClick={handleNewIngredient}
                color="primary"
              >
                {t('ingredients.addIngredient')}
              </Button>
            </TopBar>

            <StyledToolbar variant="outlined">
              <Grid display="flex" container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <SearchInput
                    id="search-ingredients"
                    placeholder={t('global.search')}
                    variant="outlined"
                    value={searchValue}
                    autoComplete="off"
                    onChange={(e) => {
                      e.preventDefault();
                      setSearchValue(e.target.value);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid display="flex" alignItems="center" item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch size="small" checked={showNotActive} onChange={toggleNotActive} />}
                    label={`${t('ingredients.showInactive')} (${notActiveCount} kpl)`}
                  />
                  <FormControlLabel
                    control={<Switch size="small" checked={showRecipeOnly} onChange={toggleRecipeOnly} />}
                    label={`${t('ingredients.showRecipeOnly')} (${recipeOnlyCount} kpl)`}
                  />
                </Grid>
              </Grid>
            </StyledToolbar>

            <DataGridContainer display="flex">
              <StyledDataGrid
                getRowHeight={() => 'auto'}
                disableRowSelectionOnClick
                rows={filteredRows || []}
                columns={columns}
                getRowId={(row) => row.ingredientId}
                getRowClassName={(params) => (params.row.isActive ? '' : 'grid-row-not-active')}
                loading={isLoading}
              />
            </DataGridContainer>
          </>
        }
      />
    </Routes>
  );
};

export const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 15px 0;
`;

const SearchInput = styled(TextField)`
  margin-right: 2em;
  flex: 0 1 500px;
  width: 100%;
`;

export const StyledToolbar = styled(Paper)`
  margin-bottom: 1em;
  display: flex;
  padding: 1em;
`;

export const DataGridContainer = styled(Box)`
  height: calc(100vh - 300px);
`;

const ErrorLink = styled(StyledLink)`
  color: ${({ theme }) => theme.palette.error.main};
`;

const IngredientInfo = styled.div`
  display: flex;
  flex-direction: column;
  ${StyledRouterLink} {
    font-weight: bold;
    padding-bottom: 0px;
  }
`;

const CategoryText = styled.p`
  margin: 0px;
`;

export default IngredientsPage;
